import { FormikProps } from "formik";
import { OpretFirmaStep1Formik } from "../../opretFirmaInterface";
import { StickyFooter } from "../../../../stickyFooter/modalStickyFooter";
import Button from "@/components/bricks/button/Button";
import { BooleanFormControl } from "../../../../formcontrols/BooleanFormControl";
import { SelectFormik } from "../../../../formcontrols/Select";
import { ISeptimaSearchResultItem } from "../../../../SeptimaSearchReact/interfaces/types";
import { FormikDatePicker } from "../../../../formcontrols/DatePicker";
import Produktionsenhed from "./Produktionsenhed";
import { NoCVRFormDKGL } from "./NoCVRFormDKGL";
import DuplicateFirmaWarning from "../DuplicateFirmaWarning";
import { useDuplicateFirmaFinder } from "../../../../../hooks/useDuplicateFirmaFinder";
import OpretFirmaUDL from "./OpretFirmaUDL";
import OpretFirmaFO from "./OpretFirmaFO";
import { IProduktionsenhed } from "../../../../../interfaces/firma";
import InputRadio from "../../../../formcontrols/InputRadio";
import ValidationErrorMessage from "../../../../formcontrols/ValidationErrorMessage";
import CvrOpslag, { useCvrOpslag } from "./CvrOpslag";

const OpretFirmaStep1 = ({
  formik,
  produktionsEnheder,
  searchResult,
  setModalOpen,
  setProduktionsEnheder,
  setSearchResult,
}: {
  setModalOpen: (open: boolean) => void;
  formik: FormikProps<OpretFirmaStep1Formik>;
  searchResult: ISeptimaSearchResultItem | undefined;
  setSearchResult: React.Dispatch<
    React.SetStateAction<ISeptimaSearchResultItem | undefined>
  >;
  produktionsEnheder: IProduktionsenhed[];
  setProduktionsEnheder: React.Dispatch<
    React.SetStateAction<IProduktionsenhed[]>
  >;
}) => {
  const { values, setFieldValue, handleSubmit, resetForm, touched, errors } =
    formik;
  const existingFirma = useDuplicateFirmaFinder(
    formik.values.inCVR !== "no",
    formik.values.produktionsenhed,
  );

  const selectItems = [
    {
      label: "Danmark",
      value: "DK",
    },
    {
      label: "Grønland",
      value: "GL",
    },
    {
      label: "Færøerne",
      value: "FO",
    },
    {
      label: "Andet udland",
      value: "UDL",
    },
  ];

  const cvrOpslagHook = useCvrOpslag(formik);
  function clearCVR() {
    setSearchResult(undefined);
    setProduktionsEnheder([]);
    setFieldValue("produktionsenhed", "");
    cvrOpslagHook.setCvrNr("");
    cvrOpslagHook.setPNr("");
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <div className="c-item-list c-modal-container-inner flex-column">
        <div className="w-100">
          <BooleanFormControl
            config={{
              component: "boolean",
              id: "advokatVirksomhed",
              label: "Advokatvirksomhed?",
            }}
            formik={formik}
            value={values.advokatVirksomhed}
          />
        </div>
        <div className="w-100">
          <SelectFormik
            config={{
              component: "select",
              id: "land",
              label: "Land",
              isRequired: true,
              placeholder: "",
            }}
            formik={formik}
            data={selectItems}
            additionalOnChange={() => {
              setFieldValue("inCVR", "yes-searchable");
            }}
          />
        </div>
        <h5 className="h5 mb-n2">Firma</h5>
        {(values.land === "DK" || values.land === "GL") && (
          <>
            <div className="w-100">
              <>
                <label className="form-check-label" htmlFor={"inCVR"}>
                  Findes firmaet i cvr-registeret?
                </label>
                <div className="mt-2 d-flex align-items-center gap-2">
                  <InputRadio
                    className={`form-check--badge ${
                      touched.inCVR && errors.inCVR ? " is-invalid" : ""
                    }`}
                    label={"Ja, søg firmanavn"}
                    name={"inCVR"}
                    id={"inCVR-option1"}
                    checked={values.inCVR === "yes-searchable"}
                    onChange={() => {
                      setFieldValue("inCVR", "yes-searchable", true);
                      clearCVR();
                    }}
                  />
                  <InputRadio
                    className={`form-check--badge ${
                      touched.inCVR && errors.inCVR ? " is-invalid" : ""
                    }`}
                    label={"Ja, angiv CVR- og P-nr"}
                    name={"inCVR"}
                    id={"inCVR-option2"}
                    checked={values.inCVR === "yes-manual-input"}
                    onChange={() => {
                      setFieldValue("inCVR", "yes-manual-input", true);
                      clearCVR();
                    }}
                  />
                  <InputRadio
                    className={`form-check--badge ${
                      touched.inCVR && errors.inCVR ? " is-invalid" : ""
                    }`}
                    label={"Nej"}
                    name={"inCVR"}
                    id={"inCVR-option3"}
                    checked={values.inCVR === "no"}
                    onChange={() => {
                      clearCVR();
                      setFieldValue("inCVR", "no", true);
                    }}
                  />
                </div>
                <ValidationErrorMessage
                  touched={touched.inCVR}
                  errorMessage={errors.inCVR}
                />
              </>
            </div>
            <div className="p-3 card pb-4 c-item-list w-100 mt-n2">
              {values.inCVR === "yes-searchable" && (
                <Produktionsenhed
                  formik={formik}
                  produktionsEnheder={produktionsEnheder}
                  searchResult={searchResult}
                  setSearchResult={setSearchResult}
                  setProduktionsEnheder={setProduktionsEnheder}
                />
              )}
              {values.inCVR === "yes-manual-input" && (
                <>
                  <CvrOpslag {...cvrOpslagHook} />
                  <ValidationErrorMessage
                    touched={touched.produktionsenhed}
                    errorMessage={
                      errors.produktionsenhed ? "Skal være sat" : ""
                    }
                  />
                </>
              )}
              {values.inCVR === "no" && <NoCVRFormDKGL formik={formik} />}
            </div>
          </>
        )}
        {values.land === "FO" && <OpretFirmaFO formik={formik} />}
        {values.land === "UDL" && <OpretFirmaUDL formik={formik} />}
        <div className="w-100">
          <FormikDatePicker
            config={{
              component: "datepicker",
              id: "periodeFraDato",
              label: "Startdato ",
              isRequired: true,
            }}
            formik={formik}
          />
        </div>
        {existingFirma && (
          <DuplicateFirmaWarning
            existingFirma={existingFirma.firma}
            onCancel={() => {
              setModalOpen(false);
              resetForm();
              setSearchResult(undefined);
            }}
            reason={existingFirma.reason}
          />
        )}
      </div>
      <StickyFooter>
        {!existingFirma && (
          <>
            <Button
              color="secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
                setSearchResult(undefined);
              }}
            >
              Annuller
            </Button>
            <Button type="submit" color="primary">
              Næste
            </Button>
          </>
        )}
      </StickyFooter>
    </form>
  );
};

export default OpretFirmaStep1;
